import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { EventAccess, EventImageKind, EventStatus, Geofence } from '@rabbitfuel/shared';
import { api, type EventDetail, type EventTeam } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { GeofencePicker } from '../components/GeofencePicker.tsx';
import { TeamPanel } from '../components/TeamPanel.tsx';
import { Footer, Topbar } from '../components/Chrome.tsx';
import { loadSession, saveSession } from '../lib/session.ts';
import { toLocalInput } from '../lib/format.ts';
import {
  BACKDROP_PRESET,
  COVER_PRESET,
  EventFields,
  ImagePicker,
  LOGO_PRESET,
  emptyEventForm,
  type EventFormValues,
} from '../components/EventForm.tsx';

const statuses: { id: EventStatus; label: string }[] = [
  { id: 'upcoming', label: 'Geplant' },
  { id: 'live', label: 'Live' },
  { id: 'finished', label: 'Beendet' },
];

/** Nachtraeglich bearbeiten -- Bilder, Angaben, Status. Nur mit PIN. */
export function EventSettings() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [access, setAccess] = useState<EventAccess | null>(null);
  const [team, setTeam] = useState<EventTeam | null>(null);
  const [fence, setFence] = useState<Geofence | null>(null);
  const [fenceDirty, setFenceDirty] = useState(false);
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [values, setValues] = useState<EventFormValues>(emptyEventForm);
  const [status, setStatus] = useState<EventStatus>('upcoming');
  const [cover, setCover] = useState<string | null | undefined>(undefined);
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [backdrop, setBackdrop] = useState<string | null | undefined>(undefined);

  const [pin, setPin] = useState(() => loadSession(slug)?.organizerPin ?? '');
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .getEvent(slug)
      .then(({ event }) => {
        setEvent(event);
        setStatus(event.status);
        setFence(event.geofence);
        setValues({
          title: event.title,
          club: event.club,
          sport: event.sport,
          discipline: event.discipline,
          location: event.location,
          country: event.country,
          language: event.language,
          startsAt: toLocalInput(event.startsAt),
          description: event.description,
        });
      })
      .catch(() => setError('Event nicht gefunden.'));
  }, [slug]);

  // Konto mit Regierecht entsperrt direkt; sonst bleibt der PIN-Weg.
  useEffect(() => {
    if (authLoading) return;
    api
      .access(slug)
      .then((r) => {
        setAccess(r.access);
        setTeam(r.team);
        if (r.access.canDirect) setUnlocked(true);
      })
      .catch(() => undefined);
  }, [slug, user, authLoading]);

  useEffect(() => {
    if (!unlocked && pin && loadSession(slug)?.organizerPin === pin) setUnlocked(true);
  }, [pin, slug, unlocked]);

  const change = (patch: Partial<EventFormValues>) => {
    setValues((v) => ({ ...v, ...patch }));
    setSaved(false);
  };

  const unlock = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // Die PIN wird serverseitig geprueft -- ein no-op-PATCH reicht dafuer.
      await api.updateEvent(slug, pin, {});
      saveSession(slug, {
        ...(loadSession(slug) ?? { role: 'organizer', displayName: event?.club ?? 'Regie' }),
        role: 'organizer',
        organizerPin: pin,
      });
      setUnlocked(true);
    } catch {
      setError('PIN stimmt nicht.');
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.updateEvent(slug, pin || undefined, {
        ...values,
        status,
        startsAt: new Date(values.startsAt).toISOString(),
      });
      if (cover !== undefined || logo !== undefined || backdrop !== undefined) {
        const body: Partial<Record<EventImageKind, string | null>> = {};
        if (cover !== undefined) body.cover = cover;
        if (logo !== undefined) body.logo = logo;
        if (backdrop !== undefined) body.backdrop = backdrop;
        await api.updateImages(slug, pin || undefined, body);
      }
      if (fenceDirty) await api.updateGeofence(slug, pin || undefined, fence);
      const fresh = await api.getEvent(slug);
      setEvent(fresh.event);
      setFence(fresh.event.geofence);
      setFenceDirty(false);
      setCover(undefined);
      setLogo(undefined);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  if (!event) {
    return (
      <div className="app">
        <Topbar />
        <main className="shell section">
          <div className={error ? 'notice' : 'empty'}>{error ?? 'Wird geladen …'}</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="app">
        <Topbar />
        <main className="section">
          <div className="shell shell--narrow">
            <p className="eyebrow">Event bearbeiten</p>
            <h1 className="page-title" style={{ marginBottom: 20 }}>{event.title}</h1>
            {!user && (
              <div className="notice notice--info" style={{ marginBottom: 16 }}>
                Melde dich mit dem Konto an, dem dieses Event gehört —{' '}
                <Link className="linklike" to={`/anmelden?weiter=${encodeURIComponent(`/events/${slug}/einstellungen`)}`}>
                  zur Anmeldung
                </Link>
                . Alternativ die Event-PIN eingeben (dann ohne Regie-Liste).
              </div>
            )}
            {user && access && !access.canDirect && (
              <div className="notice" style={{ marginBottom: 16 }}>
                <b>{user.email}</b> hat für dieses Event keine Rechte. Mit der PIN kommst du trotzdem an Bilder und
                Angaben — die Regie-Liste bleibt dem Admin vorbehalten.
              </div>
            )}
            <form className="card" onSubmit={unlock}>
              <div className="card__body stack gap-16">
                <div className="field">
                  <label htmlFor="pin">Veranstalter-PIN</label>
                  <input
                    id="pin"
                    className="input"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    inputMode="numeric"
                    autoFocus
                  />
                </div>
                {error && <div className="notice">{error}</div>}
                <button className="btn btn--block" type="submit">
                  Entsperren
                </button>
                <Link className="btn btn--ghost btn--block" to={`/events/${slug}`}>
                  Zurück zum Event
                </Link>
              </div>
            </form>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Topbar />
      <main className="section">
        <div className="shell shell--form">
          <div className="row gap-12" style={{ marginBottom: 8, flexWrap: 'wrap' }}>
            <p className="eyebrow" style={{ margin: 0 }}>
              Event bearbeiten
            </p>
            <Link to={`/events/${slug}`} className="linklike" style={{ marginLeft: 'auto' }}>
              Event-Seite ansehen
            </Link>
            <Link to={`/events/${slug}/regie`} className="linklike">
              Zur Regie
            </Link>
          </div>
          <h1 className="page-title" style={{ marginBottom: 22 }}>{event.title}</h1>

          {access?.canManage && team && (
            <TeamPanel slug={slug} team={team} onChange={setTeam} />
          )}

          <form className="card" onSubmit={save}>
            <div className="card__body stack gap-16">
              <div className="field">
                <label>Status</label>
                <div className="filters" style={{ alignSelf: 'flex-start' }}>
                  {statuses.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={status === s.id ? 'is-active' : ''}
                      onClick={() => {
                        setStatus(s.id);
                        setSaved(false);
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <span className="hint">„Live" hebt das Event auf der Startseite nach oben.</span>
              </div>

              <hr className="rule" />

              <EventFields values={values} onChange={change} />

              <hr className="rule" />

              <ImagePicker
                label="Titelbild"
                hint="Hintergrund auf der Event-Seite und der Kachel. Quer, mindestens 1200 px breit."
                preset={COVER_PRESET}
                value={cover}
                currentUrl={event.coverUrl}
                onPick={(v) => {
                  setCover(v);
                  setSaved(false);
                }}
              />
              <ImagePicker
                label="Vereinslogo"
                hint="Rundes Profilbild neben dem Titel. Quadratisch, PNG mit Transparenz geht auch."
                preset={LOGO_PRESET}
                value={logo}
                currentUrl={event.logoUrl}
                shape="square"
                onPick={(v) => {
                  setLogo(v);
                  setSaved(false);
                }}
              />
              <ImagePicker
                label="Bühnenhintergrund"
                hint="Liegt im Stream hinter dem Kamerabild — Sponsorenwand, Vereinsmotiv. Quer, 16:9. Die Regie schaltet ihn im Panel unter „Hintergrund & Bild“ ein."
                preset={BACKDROP_PRESET}
                value={backdrop}
                currentUrl={event.backdropUrl}
                onPick={(v) => {
                  setBackdrop(v);
                  setSaved(false);
                }}
              />

              <hr className="rule" />

              <div className="field">
                <label>Sendebereich</label>
                <span className="hint">
                  Wer streamen will, muss sich in diesem Bereich befinden. Das hält Zuschauer aus dem Internet
                  draußen — es ist aber kein harter Schutz, ein Standort lässt sich fälschen. Die Freigabe durch die
                  Regie bleibt der eigentliche Türsteher.
                </span>
              </div>
              <GeofencePicker
                value={fence}
                onChange={(next) => {
                  setFence(next);
                  setFenceDirty(true);
                  setSaved(false);
                }}
              />

              {error && <div className="notice">{error}</div>}
              {saved && <div className="notice notice--ok">Gespeichert.</div>}

              <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                <button className="btn btn--lg" type="submit" disabled={busy}>
                  {busy ? 'Speichert …' : 'Änderungen speichern'}
                </button>
                <button
                  type="button"
                  className="btn btn--lg btn--ghost"
                  onClick={() => navigate(`/events/${slug}`)}
                >
                  Fertig
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
